// V2.1A Step C — Orchestrator SAL (Modal principale su Subcontract)
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import SubcontractSALProgress from './SubcontractSALProgress';
import SubcontractSALList from './SubcontractSALList';
import SubcontractSALWizard from './SubcontractSALWizard';
import SubcontractSALDetail from './SubcontractSALDetail';
import {
  salApi, extractApiError, formatEuro, computeSubcontractSalSummary,
  SAL_STATUS_LABELS,
} from './salApi';

const API = process.env.REACT_APP_BACKEND_URL;

const SubcontractSALTab = ({ open, onClose, subcontract }) => {
  const [sals, setSals] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedSal, setSelectedSal] = useState(null);
  const [error, setError] = useState(null);

  const canCreate = subcontract?.status === 'firmato' && availability?.can_create_sal;

  const reload = useCallback(async () => {
    if (!subcontract?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [listR, availR] = await Promise.all([
        salApi.list(subcontract.id),
        salApi.availability(subcontract.id),
      ]);
      setSals(listR.data?.items || []);
      setAvailability(availR.data);
    } catch (e) {
      const info = extractApiError(e);
      setError(info.message);
    } finally {
      setLoading(false);
    }
  }, [subcontract?.id]);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  const summary = computeSubcontractSalSummary(
    sals,
    availability?.contract_total ?? subcontract?.total_amount_net,
  );

  const reloadSelectedSal = async (updated) => {
    if (updated?.id) {
      setSelectedSal(updated);
    }
    await reload();
  };

  const lastApproved = [...sals].reverse().find(s => s.status === 'approvato' || s.status === 'autorizzato_fattura');

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="sal-tab-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>SAL Subappalto — {subcontract?.subcontract_number || subcontract?.number}</span>
              {canCreate && (
                <Button
                  size="sm"
                  onClick={() => setWizardOpen(true)}
                  data-testid="sal-new-btn"
                  className="ml-auto"
                >
                  + Nuovo SAL
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Header info */}
          <div className="text-xs text-neutral-600 grid grid-cols-2 md:grid-cols-4 gap-2 pb-2 border-b border-neutral-200">
            <div><b>Fornitore:</b> {subcontract?.supplier_snapshot?.legal_name || subcontract?.supplier_name || '—'}</div>
            <div><b>Stato:</b> {subcontract?.status}</div>
            <div><b>Contratto:</b> {formatEuro(subcontract?.total_amount_net)}</div>
            <div><b>Ritenuta:</b> {subcontract?.ritenuta_garanzia_percent}%</div>
            <div><b>N. SAL:</b> {sals.length}</div>
            <div><b>Ultimo approvato:</b> {lastApproved ? lastApproved.sal_number : '—'}</div>
          </div>

          {error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="sal-tab-error">
              {error}
            </div>
          )}
          {loading && !sals.length && <div className="text-sm text-neutral-500 py-4">Caricamento…</div>}

          {/* Progress */}
          <SubcontractSALProgress summary={summary} />

          {/* List */}
          <SubcontractSALList
            sals={sals}
            contractTotal={summary.contract_total}
            onSelect={setSelectedSal}
            onNewSal={() => setWizardOpen(true)}
            canCreate={canCreate}
          />
        </DialogContent>
      </Dialog>

      <SubcontractSALWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        subcontractId={subcontract?.id}
        onCreated={() => reload()}
      />

      <SubcontractSALDetail
        open={!!selectedSal}
        onClose={() => setSelectedSal(null)}
        sal={selectedSal}
        onChanged={reloadSelectedSal}
      />
    </>
  );
};

/**
 * Utility hook: legge il feature flag SAL dal company_settings.
 * Cache in memoria per la sessione (basta per un flag di rollout).
 */
let _flagCache = null;
export const useSubcontractSalV2Enabled = () => {
  const [enabled, setEnabled] = useState(_flagCache);
  useEffect(() => {
    if (_flagCache !== null) { setEnabled(_flagCache); return; }
    axios.get(`${API}/api/settings/company`, { withCredentials: true })
      .then(r => {
        _flagCache = !!r.data?.subcontract_sal_v2_enabled;
        setEnabled(_flagCache);
      })
      .catch(() => setEnabled(false));
  }, []);
  return enabled;
};

export default SubcontractSALTab;
